import React from 'react';
import { Controller, FieldValues, useFormState } from 'react-hook-form';
import { BaseFormFieldProps } from './types';
import RoleSelector from '../selectors/RoleSelector';

export interface RoleSelectorComponentProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
}

export interface FormRoleSelectorProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<BaseFormFieldProps<TFieldValues>, 'label' | 'placeholder'> {
  placeholder?: string;
  label?: string;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
}

const FormRoleSelector = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  isRequired = false,
  showErrorOnTouched = true,
  placeholder,
  label,
  showEmptyOption = false,
  isDisabled = false,
  ...rest
}: FormRoleSelectorProps<TFieldValues>) => {
  const { touchedFields, errors, isSubmitted } = useFormState({ control });
  const fieldError = errors[name]?.message as string | undefined;
  const isTouched = touchedFields[name];
  const showError = showErrorOnTouched ? (isTouched || isSubmitted) && !!fieldError : !!fieldError;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <RoleSelector
          value={field.value}
          onChange={field.onChange}
          isRequired={isRequired}
          isInvalid={showError}
          errorMessage={showError ? fieldError : undefined}
          placeholder={placeholder}
          label={label}
          showEmptyOption={showEmptyOption}
          isDisabled={isDisabled}
          {...rest}
        />
      )}
    />
  );
};

FormRoleSelector.displayName = 'FormRoleSelector';

export default FormRoleSelector;

